'use client'

import { useEffect, useId, useRef, useState } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { cx } from '@/app/lib/utils'

import {
  DEFAULT_COLLAPSED_LINES,
  LABEL_COLLAPSE,
  LABEL_EXPAND,
  LINE_HEIGHT_FALLBACK_RATIO,
  OVERFLOW_TOLERANCE_PX,
} from './CMSExpandableText.constants'
import styles from './CMSExpandableText.module.scss'
import type { CMSExpandableTextProps } from './CMSExpandableText.types'

/**
 * CMSExpandableText [atom] — DS Entradas.
 *
 * Texto de CMS que se recorta a N líneas (colapsado) y se expande con un
 * disparador "Ver más / Ver menos". El disparador solo aparece si el texto
 * realmente desborda el número de líneas indicado.
 *
 * Client Component: necesita estado, refs y medición en el DOM
 * (`getComputedStyle` + `ResizeObserver`), por lo que no puede ser RSC.
 *
 * Dos vías mutuamente excluyentes (unión discriminada, ver `.types.ts`):
 *
 * - `text` — contenido de texto plano, renderizado como children de React, que
 *   lo escapa. Sin riesgo de XSS y sin necesidad de sanitización.
 * - `html` — HTML enriquecido del CMS, insertado con `dangerouslySetInnerHTML`.
 *   **DEBE llegar ya saneado** con `sanitizeHtml`, ejecutado en el servidor;
 *   este componente no sanea. Se añadió para la sinopsis y los textos largos de
 *   la ficha de evento (specs/043-pagina-de-producto, FR-028/FR-038), que
 *   pierden negritas y enlaces si se degradan a texto plano.
 *
 * Estrategia de medición (heredada de la fuente):
 * - Un único `<p>`. Para medir la altura natural se retira temporalmente la
 *   restricción `max-height` en el mismo hilo síncrono (sin paint intermedio,
 *   sin parpadeo).
 * - `ResizeObserver` re-mide al cambiar el ancho del contenedor (responsive).
 * - Idempotente en Strict Mode: siempre mide la altura sin restricción.
 *
 * Accesibilidad (WCAG 2.1 AA — 1.3.1, 2.1.1, 2.4.7, 4.1.2):
 * - El disparador es un `<button aria-expanded aria-controls>` (operable por
 *   teclado de forma nativa) que apunta al párrafo que controla.
 * - Foco visible vía mixin `action-link` (`focus-visible`).
 * - Respeta `prefers-reduced-motion`.
 *
 * Analítica: cada alternancia dispara un `ui_interaction` de tipo `accordion`
 * (`component_action: expand | collapse`), el patrón que fija
 * `components-fingerprint/Entradas + FAQs.csv` para un bloque desplegable
 * (mismo criterio ya usado en `ExpandableDescription` y `Accordion`). Solo se
 * emite si el llamador pasa `analytics` (`CMSExpandableTextAnalyticsConfig`);
 * sin ella, o fuera de `<PageAnalyticsProvider>`, no cambia nada de lo
 * existente (`useOptionalPageAnalytics` devuelve `null`).
 */
export function CMSExpandableText(props: CMSExpandableTextProps) {
  const {
    collapsedLines = DEFAULT_COLLAPSED_LINES,
    trailing,
    tone = 'default',
    className,
    style,
    analytics: analyticsConfig,
  } = props
  const analytics = useOptionalPageAnalytics()

  // Contenido efectivo, sea cual sea la vía: gobierna la medición y el reset.
  const content = props.html ?? props.text ?? ''

  const [open, setOpen] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null)
  const textRef = useRef<HTMLElement>(null)

  // Reset del estado abierto al cambiar el contenido: ajuste en render (patrón
  // de React para "estado derivado de props"), no en un efecto.
  const [prevContent, setPrevContent] = useState(content)
  if (content !== prevContent) {
    setPrevContent(content)
    setOpen(false)
  }

  const textId = useId()

  useEffect(() => {
    const el = textRef.current
    if (!el) return

    const measure = () => {
      // 1. Quitar temporalmente la restricción para medir la altura natural.
      const prevMaxHeight = el.style.maxHeight
      const prevOverflow = el.style.overflow
      el.style.maxHeight = 'none'
      el.style.overflow = 'visible'

      // 2. Leer line-height real y scrollHeight natural.
      const computed = getComputedStyle(el)
      let lineHeight = parseFloat(computed.lineHeight)
      if (Number.isNaN(lineHeight)) {
        lineHeight = parseFloat(computed.fontSize) * LINE_HEIGHT_FALLBACK_RATIO
      }

      const naturalHeight = el.scrollHeight
      const maxHeight = lineHeight * collapsedLines

      // 3. Restaurar (React reescribirá el inline style en el siguiente render).
      el.style.maxHeight = prevMaxHeight
      el.style.overflow = prevOverflow

      // 4. Actualizar estado.
      setCollapsedHeight(maxHeight)
      setIsOverflowing(naturalHeight > maxHeight + OVERFLOW_TOLERANCE_PX)
    }

    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [content, collapsedLines])


  function handleToggle() {
    const nextOpen = !open
    if (analyticsConfig) {
      analytics?.trackUiInteraction({
        component_type: 'accordion',
        component_action: nextOpen ? 'expand' : 'collapse',
        component_id: analyticsConfig.componentId,
        component_label: slugify(analyticsConfig.componentLabel),
        selected_value: NO_APLICA,
        module_name: analyticsConfig.moduleName,
        module_type: analyticsConfig.moduleType,
        module_position: analyticsConfig.modulePosition,
      })
    }
    setOpen(nextOpen)
  }

  const isClamped = !open && isOverflowing && collapsedHeight !== null
  const collapsedStyle = isClamped
    ? { maxHeight: `${collapsedHeight}px`, overflow: 'hidden' as const }
    : undefined

  // Elemento distinto por vía: el HTML del CMS trae bloques (`<p>`, listas,
  // encabezados) que serían inválidos anidados dentro de un `<p>`.
  const body =
    props.html !== undefined ? (
      <div
        id={textId}
        ref={textRef as React.RefObject<HTMLDivElement>}
        className={styles.text}
        style={collapsedStyle}
        // El HTML del CMS puede traer `<a>` (ALLOWED_TAGS de sanitizeHtml).
        // Recortado solo con `overflow: hidden` esos enlaces siguen alcanzables
        // por Tab aunque queden fuera de las 3 líneas visibles — `inert`
        // mientras está plegado los saca del foco y del árbol de accesibilidad
        // a la vez que la vía `text` (sin elementos interactivos, nunca lo
        // necesitó).
        inert={isClamped}
        // Ya saneado por el llamador (servidor) — ver CMSExpandableText.types.ts.
        dangerouslySetInnerHTML={{ __html: props.html }}
      />
    ) : (
      <p
        id={textId}
        ref={textRef as React.RefObject<HTMLParagraphElement>}
        className={styles.text}
        style={collapsedStyle}
      >
        {props.text}
      </p>
    )

  return (
    <div
      className={cx(styles.root, tone === 'inverse' && styles.rootInverse, className)}
      style={style}
    >
      {body}

      {(isOverflowing || trailing) && (
        <div className={styles.actions}>
          {isOverflowing && (
            <button
              type="button"
              className={styles.trigger}
              onClick={handleToggle}
              aria-expanded={open}
              aria-controls={textId}
            >
              {open ? LABEL_COLLAPSE : LABEL_EXPAND}
            </button>
          )}
          {/* Separador puramente visual: solo tiene sentido entre dos acciones,
              y sin disparador el `trailing` va suelto. */}
          {isOverflowing && trailing && (
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
          )}
          {trailing}
        </div>
      )}
    </div>
  )
}
